import type { TourType } from '@/config/objects/tour-types';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Map, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Map },
  { label: 'Tours', path: 'tours', icon: Map },
];

export function TourTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: TourType }>({
    queryKey: ['TourType', id],
    queryFn: () => apiClient.get(`/tour-types/${id}`),
    enabled: !!id,
  });

  const tourType = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (tourTypeId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/tour-types/${tourTypeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['TourType'] });
        navigate('/tour-types');
      } else {
        throw new Error(response.error || 'Failed to delete tour type');
      }
    },
  });

  const handleDelete = () => {
    if (!tourType) return;
    toast.promise(deleteMutation.mutateAsync(tourType.id), {
      loading: 'Deleting tour type...',
      success: 'Tour type deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete tour type',
    });
  };

  useEffect(() => {
    if (id) {
      const basePath = `/tour-types/${id}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [id, location.pathname, navigate]);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Tour Types', href: '/tour-types' },
    { label: tourType?.name || 'Tour Type', current: true },
  ];

  const basePath = `/tour-types/${id}`;
  const imageUrl = tourType?.featuredImage?.url ?? null;

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Tour Type Not Found</h2>
          <p className="text-muted-foreground">
            The requested tour type could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          tourType && (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Tour Type', icon: Map }}
        title={tourType?.name || 'Tour Type'}
        image={{
          url: imageUrl,
          alt: tourType?.name || 'Tour Type',
          fallbackIcon: Map,
        }}
        tabs={tabs}
        basePath={basePath}
      >
        <Outlet context={{ tourType, isLoading }} />
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Tour Type"
        description={`Are you sure you want to delete "${tourType?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
